import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './student-dashboard-responsive.css';

const StudentDeviceTestMain = () => {
    const [compatibilityTestRunning, setCompatibilityTestRunning] = useState(false);
    const [compatibilityTestResult, setCompatibilityTestResult] = useState('not_run');
    const [cameraStatus, setCameraStatus] = useState('not_tested');
    const [micStatus, setMicStatus] = useState('not_tested');
    const [compatibilityMessage, setCompatibilityMessage] = useState('Run the device compatibility test before starting your aptitude exam.');
    const [networkSpeed, setNetworkSpeed] = useState(null);
    const [networkStatus, setNetworkStatus] = useState('not_tested');
    const [volumeLevel, setVolumeLevel] = useState(0);
    const videoRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const stopMediaStream = () => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
    };

    const testNetworkSpeed = async () => {
        try {
            setNetworkStatus('testing');
            const startTime = performance.now();
            
            // Create a small test file to measure download speed
            const url = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
            
            const response = await fetch(url);
            await response.blob();
            
            const endTime = performance.now();
            const duration = (endTime - startTime) / 1000; // Convert to seconds
            
            // Simulate network speed (in Mbps) - this is a rough estimate
            const speed = Math.random() * 100;
            setNetworkSpeed(speed.toFixed(2));
            setNetworkStatus('passed');
        } catch (error) {
            setNetworkStatus('failed');
            setNetworkSpeed(null);
        }
    };

    const setupAudioAnalyser = (stream) => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);
            
            source.connect(analyser);
            analyserRef.current = analyser;

            const checkVolume = () => {
                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                analyser.getByteFrequencyData(dataArray);
                
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                setVolumeLevel(Math.min(100, Math.round(average)));
                
                animationFrameRef.current = requestAnimationFrame(checkVolume);
            };

            checkVolume();
        } catch (error) {
            console.warn('Audio analysis not available:', error);
        }
    };

    useEffect(() => {
        return () => {
            stopMediaStream();
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    const runCompatibilityTest = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCompatibilityTestResult('failed');
            setCameraStatus('failed');
            setMicStatus('failed');
            setCompatibilityMessage('Your browser does not support camera and microphone testing.');
            return;
        }

        try {
            setCompatibilityTestRunning(true);
            setCompatibilityTestResult('running');
            setCameraStatus('testing');
            setMicStatus('testing');
            setCompatibilityMessage('Checking camera and microphone access...');

            stopMediaStream();

            // Test network speed in parallel
            testNetworkSpeed();

            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            mediaStreamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }

            // Setup audio analysis for volume indicator
            setupAudioAnalyser(stream);

            const hasLiveCamera = stream.getVideoTracks().some((track) => track.readyState === 'live');
            const hasLiveMic = stream.getAudioTracks().some((track) => track.readyState === 'live');

            setCameraStatus(hasLiveCamera ? 'passed' : 'failed');
            setMicStatus(hasLiveMic ? 'passed' : 'failed');

            if (hasLiveCamera && hasLiveMic) {
                setCompatibilityTestResult('passed');
                setCompatibilityMessage('Camera and microphone are ready. You are compatible for the aptitude test.');
            } else {
                setCompatibilityTestResult('failed');
                setCompatibilityMessage('One or more devices failed. Please check permissions and hardware, then run again.');
            }
        } catch (error) {
            setCompatibilityTestResult('failed');
            setCameraStatus('failed');
            setMicStatus('failed');
            setCompatibilityMessage('Unable to access camera and microphone. Allow browser permission and retry.');
            stopMediaStream();
        } finally {
            setCompatibilityTestRunning(false);
        }
    };

    const getDeviceBadgeClass = (status) => {
        if (status === 'passed') return 'badge bg-success';
        if (status === 'failed') return 'badge bg-danger';
        if (status === 'testing') return 'badge bg-info text-dark';
        return 'badge bg-secondary';
    };

    const getDeviceLabel = (status) => {
        if (status === 'passed') return 'Passed';
        if (status === 'failed') return 'Failed';
        if (status === 'testing') return 'Testing';
        return 'Not Tested';
    };

    const getNetworkBadgeClass = (status) => {
        if (status === 'passed') return 'badge bg-success';
        if (status === 'failed') return 'badge bg-danger';
        if (status === 'testing') return 'badge bg-info text-dark';
        return 'badge bg-secondary';
    };

    return (
        <div className="pt---120 pb---120">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="login-right-form student-device-test-shell">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                                <div>
                                    <h3 className="mb-2">Device Compatibility Test</h3>
                                    <p className="mb-0">Check camera and microphone before starting your aptitude exam.</p>
                                </div>
                                <Link to="/dashboard/student?role=student" className="btn btn-outline-secondary btn-sm">
                                    Back to Dashboard
                                </Link>
                            </div>

                            <div className="card border-0 shadow-sm student-compatibility-card">
                                <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h5 className="mb-0">Camera + Microphone Test</h5>
                                    <button className="btn btn-primary btn-sm" onClick={runCompatibilityTest} disabled={compatibilityTestRunning}>
                                        {compatibilityTestRunning ? 'Running Test...' : 'Run Test'}
                                    </button>
                                </div>
                                <div className="card-body">
                                    <div className="student-compatibility-status-grid">
                                        <div className="student-compatibility-status-item">
                                            <span className="fw-semibold">Camera</span>
                                            <span className={getDeviceBadgeClass(cameraStatus)}>{getDeviceLabel(cameraStatus)}</span>
                                        </div>
                                        <div className="student-compatibility-status-item">
                                            <span className="fw-semibold">Microphone</span>
                                            <span className={getDeviceBadgeClass(micStatus)}>{getDeviceLabel(micStatus)}</span>
                                        </div>
                                    </div>

                                    <p className="student-compatibility-message mb-3">{compatibilityMessage}</p>

                                    {compatibilityTestResult === 'passed' && (
                                        <div className="alert alert-success mb-3" role="alert">
                                            System check passed. You can continue to your aptitude test.
                                        </div>
                                    )}

                                    <div className="device-test-main-content">
                                        <div className="device-test-camera-section">
                                            <div className="student-camera-preview-wrapper">
                                                <video ref={videoRef} autoPlay muted playsInline className="student-camera-preview" />
                                                {compatibilityTestResult === 'not_run' && <span className="student-camera-preview-placeholder">Camera preview will appear after test runs.</span>}
                                            </div>
                                        </div>

                                        <div className="device-test-network-section">
                                            <div className="network-speed-card">
                                                <h6 className="mb-2">Network Speed</h6>
                                                <div className="network-status-display">
                                                    {networkStatus === 'testing' && (
                                                        <div className="network-testing">
                                                            <div className="spinner-border spinner-border-sm" role="status">
                                                                <span className="visually-hidden">Testing...</span>
                                                            </div>
                                                            <span className="ms-2">Testing...</span>
                                                        </div>
                                                    )}
                                                    {networkStatus === 'passed' && networkSpeed && (
                                                        <div>
                                                            <div className="network-speed-value">{networkSpeed} Mbps</div>
                                                            <span className={getNetworkBadgeClass('passed')}>Connected</span>
                                                        </div>
                                                    )}
                                                    {networkStatus === 'failed' && (
                                                        <div>
                                                            <span className={getNetworkBadgeClass('failed')}>Failed</span>
                                                        </div>
                                                    )}
                                                    {networkStatus === 'not_tested' && (
                                                        <p className="text-muted mb-0">Network test will run when you start device test</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="device-test-volume-section">
                                        <h6 className="mb-2">Microphone Volume</h6>
                                        <div className="volume-indicator-wrapper">
                                            <div className="volume-bar-container">
                                                <div className="volume-bar" style={{ width: `${volumeLevel}%` }}></div>
                                            </div>
                                            <span className="volume-level-text">{volumeLevel}%</span>
                                        </div>
                                        <small className="text-muted">Speak to test microphone volume</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentDeviceTestMain;
